/**
 * Attachment Management Modal Component
 * 
 * Main modal for managing PDF attachments (exhibits) for documents, chapters, sections, and variants.
 * Combines upload functionality with attachment list management.
 * 
 * Features:
 * - Multi-file PDF upload
 * - Attachment list with drag-and-drop reordering
 * - Edit attachment names
 * - Delete attachments
 * - Download attachments
 * - Real-time exhibit numbering
 */

import React, { useEffect, useState, useCallback } from 'react';
import { notify } from '@NOTIFICATION/Notificator';

import classnames from 'classnames/bind';

import { Attachment } from '@INTERFACES/api/attachments';

import useAttachmentManagementModal from '@HOOKS/store/modals/useAttachmentManagementModal';

import BaseModal from '@MODALS/BaseModal';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import MultiFilePDFDropzone from '@COMPONENTS/COMMON/inputs/file/MultiFilePDFDropzone';
import AttachmentList from '@COMPONENTS/SHARED/AttachmentList';
import { attachmentsAdminApi } from '../../../SERVICES/admin-api/attachments.admin-api';

import styles from './AttachmentManagementModal.module.scss';

const cx: CX = classnames.bind(styles);

function AttachmentManagementModal() {
    const {
        state: { isOpen, data: modalData },
        actions: { close },
    } = useAttachmentManagementModal();

    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    // API hooks
    const {
        data: attachmentsResponse,
        isLoading: isLoadingAttachments,
        refetch,
    } = attachmentsAdminApi.useGetAttachmentsQuery(
        {
            content_type: modalData?.parentType,
            object_id: modalData?.parentId,
        },
        {
            skip: !isOpen || !modalData,
        },
    );

    const [createAttachment] = attachmentsAdminApi.useCreateAttachmentMutation();
    const [updateAttachment] = attachmentsAdminApi.useUpdateAttachmentMutation();
    const [deleteAttachment] = attachmentsAdminApi.useDeleteAttachmentMutation();
    const [reorderAttachments] = attachmentsAdminApi.useReorderAttachmentsMutation();

    const attachments = attachmentsResponse || [];

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setPendingFiles([]);
            setIsUploading(false);
        }
    }, [isOpen]);

    // Handle file selection from dropzone
    const handleFilesSelected = useCallback((files: File[]) => {
        setPendingFiles((prev) => [...prev, ...files]);
    }, []);

    // Upload pending files
    const handleUploadFiles = useCallback(async () => {
        if (!modalData || pendingFiles.length === 0) return;

        setIsUploading(true);
        
        try {
            const uploadPromises = pendingFiles.map((file) => createAttachment({
                name: file.name.replace(/\.pdf$/i, ''), // Remove .pdf extension
                file,
                content_type_name: modalData.parentType,
                object_id: modalData.parentId,
                // order field removed - backend will auto-assign next available order
            }).unwrap());

            await Promise.all(uploadPromises);
            
            notify.success(`Successfully uploaded ${pendingFiles.length} attachment${pendingFiles.length !== 1 ? 's' : ''}!`);
            setPendingFiles([]);
            refetch();
        } catch (error: any) {
            notify.error(error?.data?.message || 'Failed to upload attachments');
        } finally {
            setIsUploading(false);
        }
    }, [modalData, pendingFiles, createAttachment, refetch]);

    // Handle attachment reordering
    const handleReorder = useCallback(async (reorderedAttachments: Attachment[]) => {
        try {
            const attachmentIds = reorderedAttachments.map((attachment) => attachment.id);
            await reorderAttachments({ attachment_ids: attachmentIds }).unwrap();
            
            notify.success('Attachment order updated successfully!');
            refetch();
        } catch (error: any) {
            notify.error(error?.data?.message || 'Failed to reorder attachments');
        }
    }, [reorderAttachments, refetch]);

    // Handle attachment name edit
    const handleEdit = useCallback(async (attachment: Attachment) => {
        try {
            await updateAttachment({
                id: attachment.id,
                name: attachment.name,
            }).unwrap();
            
            notify.success('Attachment name updated successfully!');
            refetch();
        } catch (error: any) {
            notify.error(error?.data?.message || 'Failed to update attachment');
        }
    }, [updateAttachment, refetch]);

    // Handle attachment deletion
    const handleDelete = useCallback(async (attachment: Attachment) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Are you sure you want to delete "${attachment.name}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await deleteAttachment(attachment.id).unwrap();
            
            notify.success('Attachment deleted successfully!');
            refetch();
        } catch (error: any) {
            notify.error(error?.data?.message || 'Failed to delete attachment');
        }
    }, [deleteAttachment, refetch]);

    // Handle attachment download
    const handleDownload = useCallback((attachment: Attachment) => {
        // Open the file URL in a new tab to trigger download
        window.open(attachment.file_url, '_blank');
    }, []);

    // Remove pending file
    const removePendingFile = useCallback((index: number) => {
        setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    };

    if (!isOpen || !modalData) {
        return null;
    }

    return (
        <BaseModal
            onClose={close}
            style={{
                width: 'calc(100% - 60px)',
                maxWidth: '900px',
            }}
        >
            <div className={cx('attachment-management-modal')}>
                <div className={cx('modal-header')}>
                    <div className={cx('header-content')}>
                        <h2 className={cx('modal-title')}>
                            Manage Attachments
                        </h2>
                        <div className={cx('header-subtitle')}>
                            {modalData.parentType.charAt(0).toUpperCase() + modalData.parentType.slice(1)}
                            :
                            {' '}
                            {modalData.parentName}
                        </div>
                        <div className={cx('header-note')}>
                            These attachments will be printed into generated documents and listed as exhibits. They are not for internal use.
                        </div>
                    </div>
                    <div className={cx('attachment-count-badge')}>
                        {attachments.length}
                        {' '}
                        attachment
                        {attachments.length !== 1 ? 's' : ''}
                    </div>
                </div>

                <div className={cx('modal-content')}>
                    {/* Upload Section */}
                    {modalData.canEdit && (
                        <div className={cx('upload-section')}>
                            <h3 className={cx('section-title')}>Upload New Attachments</h3>
                            
                            <MultiFilePDFDropzone
                                onFilesSelected={handleFilesSelected}
                                disabled={isUploading}
                                helpText="Upload PDF files to add as exhibits. They will be numbered automatically."
                            />

                            {/* Pending Files List */}
                            {pendingFiles.length > 0 && (
                                <div className={cx('pending-files')}>
                                    <div className={cx('pending-header')}>
                                        <span className={cx('pending-title')}>
                                            {pendingFiles.length}
                                            {' '}
                                            file
                                            {pendingFiles.length !== 1 ? 's' : ''}
                                            {' '}
                                            ready to upload
                                        </span>
                                        <BasicButton
                                            variant="neutral"
                                            size="small"
                                            title="Clear All"
                                            onClick={() => setPendingFiles([])}
                                            locked={isUploading}
                                            style={{ width: 'auto', minWidth: '80px' }}
                                        />
                                    </div>
                                    
                                    <div className={cx('pending-list')}>
                                        {pendingFiles.map((file, index) => (
                                            <div key={`${file.name}-${file.size}-${file.lastModified}`} className={cx('pending-item')}>
                                                <div className={cx('pending-file-icon')}>
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" fill="currentColor" />
                                                    </svg>
                                                </div>
                                                <div className={cx('pending-file-details')}>
                                                    <div className={cx('pending-file-name')}>{file.name}</div>
                                                    <div className={cx('pending-file-size')}>{formatFileSize(file.size)}</div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removePendingFile(index)}
                                                    className={cx('remove-pending-btn')}
                                                    disabled={isUploading}
                                                    title="Remove file"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    <div className={cx('upload-actions')}>
                                        <BasicButton
                                            variant="success"
                                            title={isUploading ? 'Uploading...' : `Upload ${pendingFiles.length} File${pendingFiles.length !== 1 ? 's' : ''}`}
                                            onClick={handleUploadFiles}
                                            isProcessing={isUploading}
                                            locked={pendingFiles.length === 0}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Attachments List Section */}
                    <div className={cx('attachments-section')}>
                        <h3 className={cx('section-title')}>Current Attachments</h3>
                        
                        <AttachmentList
                            attachments={attachments}
                            onReorder={handleReorder}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onDownload={handleDownload}
                            isLoading={isLoadingAttachments}
                            canEdit={modalData.canEdit}
                        />
                    </div>
                </div>

                <div className={cx('modal-footer')}>
                    <BasicButton
                        variant="neutral"
                        title="Close"
                        onClick={close}
                    />
                </div>
            </div>
        </BaseModal>
    );
}

export default AttachmentManagementModal; 